/**
 * Parent-reachable logo writers for the command bar (Logo Initiative LG-3).
 *
 * `editable-capabilities.js` grants a type `controls` only where a real writer
 * has been verified to exist AND to be reachable from the parent. Every logo
 * writer already existed as a module-scope export of `editing/logo-editor.js`;
 * what was missing was a seam the bar could call, which is why `logo` sat at
 * `controls: []` with the "no lifted writer yet" note. This module is that seam.
 *
 * ── Why the logo editor is INJECTED ───────────────────────────────────────
 * Same reason as `image-command-service.js`: importing `editing/logo-editor.js`
 * at module scope pulls `image-picker.js` and `image-editor.js`, whose graph
 * starts fetching site-effect scripts on load. Under node that is a wall of
 * caught-and-logged failures, and a gate suite is exactly where a real error
 * goes to hide in that noise.
 *
 * ── Why these calls are NOT stateless, unlike the image service ────────────
 * `image.replace(payload)` takes its target as an argument. Every logo writer
 * instead reads a module-level `activeLogoEdit`, set from only two places — the
 * glass panel when it opens, and the drag handles on a rect event. A control on
 * the command bar is neither, so each entry point here seeds that state from the
 * payload it was given before delegating. The alternative was to re-plumb every
 * writer in `logo-editor.js` to take a target, which is a larger change than the
 * one this phase is making and would touch the drag path mid-gesture.
 */

import { store } from '../../core/state.js';
import { eventBus } from '../../core/events.js';
import { resolveSectionByInstance } from '../../editing/section-utils.js';

const NOOP_LOGO_EDITOR = Object.freeze({
  setActiveLogoEdit: () => {},
  getActiveLogoEdit: () => null,
  replaceLogo: async () => {},
  setLogoTransform: () => null,
  getLogoInfoFromHTML: () => null
});

let _logo = NOOP_LOGO_EDITOR;

/**
 * Install the real logo editor. `ui/shell.js` wires it at boot.
 * @param {object|null} impl — null restores the no-op default.
 */
export function setLogoEditorImpl(impl) {
  _logo = impl || NOOP_LOGO_EDITOR;
}

/** Actions this service can actually perform. Mirrors editable-capabilities. */
export const LOGO_ACTIONS = Object.freeze(['replace', 'size', 'alt']);

/**
 * Point `logo-editor`'s module state at this payload.
 * @returns {boolean} false when the payload is not a usable logo target.
 */
function target(payload) {
  if (!payload?.instanceId || payload.editableType !== 'logo') return false;
  _logo.setActiveLogoEdit(payload);
  return true;
}

/**
 * Open the picker for this logo and swap it.
 *
 * `replaceLogo` owns the picker, the upload path, the asset URI, the fan-out to
 * every logo slot in the section and the aspect-box re-measure (LG-0). None of
 * that is re-implemented here.
 */
export async function replace(payload) {
  if (!target(payload)) return;
  await _logo.replaceLogo({});
}

/**
 * Set the logo's rendered height.
 *
 * Height, not width, because a site header is a horizontal band: height is the
 * constrained dimension, so it is the one with a meaningful maximum, and width
 * is a consequence of the asset. `setLogoTransform` clamps the value to the
 * anchor and pairs it with `width: auto` so the aspect ratio can derive the
 * width — callers do not need to do either.
 *
 * @param {object} payload — the active-editable payload
 * @param {number|null} px — null/0 clears the authored height
 */
export function setSize(payload, px) {
  if (!target(payload)) return false;
  const value = Number(px);
  const slotHeight = Number.isFinite(value) && value > 0 ? `${Math.round(value)}px` : '';
  store.beginTransaction('Resize logo');
  try {
    _logo.setLogoTransform({ slotHeight });
    store.commitTransaction();
  } catch (err) {
    store.abortTransaction();
    throw err;
  }
  eventBus.emit('editing:logo-mutated', { label: 'Logo resized' });
  return true;
}

/**
 * The logo's CURRENT rendered height in px, for the stepper's readback.
 *
 * Measured, not authored — the field should show what the reader sees, the same
 * choice the text size stepper makes with `computedFontSize`. An unauthored logo
 * has no stored height at all, so an authored-value readback would show a blank
 * field on every untouched logo.
 */
export function currentSizePx(payload) {
  // The CLICK payload carries no geometry — `logoSlotRect` rides the separate
  // `element-rect` message, and `logo-editor` is what holds the latest one
  // (kept current by LG-2b). Reading only the payload rendered an empty field on
  // every real selection, which the browser gate caught; the node suite could
  // not, because it mounts the pane with a payload it fabricates itself.
  const live = _logo.getActiveLogoEdit?.();
  const sameTarget = live && payload?.eid ? live.eid === payload.eid : !!live;
  const candidates = [
    payload?.logoSlotRect?.height,
    payload?.rect?.height,
    sameTarget ? live?.logoSlotRect?.height : undefined,
    sameTarget ? live?.rect?.height : undefined
  ];
  for (const candidate of candidates) {
    const height = Number(candidate);
    if (Number.isFinite(height) && height > 0) return Math.round(height);
  }
  return null;
}

/**
 * Does this logo carry a description?
 *
 * Read from the canonical section HTML rather than the click payload: the
 * payload is a snapshot, and a replace rewrites the alt beneath it. Returns null
 * when it cannot tell, which the caller must treat as neutral rather than as a
 * reported accessibility defect.
 */
export function hasAlt(payload) {
  if (!payload?.instanceId) return null;
  const resolved = resolveSectionByInstance(payload.instanceId);
  if (!resolved?.section) return null;
  const info = _logo.getLogoInfoFromHTML(resolved.section.html, payload);
  if (!info) return null;
  if (info.ariaLabel) return true;
  return (info.images || []).some((img) => img.alt);
}
